import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Users, TrendingUp, Phone, MessageCircle, ShieldCheck, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { LEAD_STATUS_LABEL, LEAD_STATUSES } from "@/lib/i18n";
import DLDRefreshPanel from "@/components/admin/DLDRefreshPanel";
import RoniModelPanel from "@/components/admin/RoniModelPanel";
import RoniHealthPanel from "@/components/admin/RoniHealthPanel";

export default function AdminDashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [recent, setRecent] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [pendingOtp, setPendingOtp] = useState(null);

  const load = async () => {
    try {
      const [s, l, tr] = await Promise.all([
        api.get("/leads/stats"),
        api.get("/leads"),
        api.get("/stats/summary").catch(() => null),
      ]);
      setStats(s.data);
      setRecent((l.data || []).slice(0, 5));
      if (tr?.data) setTraffic(tr.data);
    } catch { /* noop */ }
  };
  // Poll the latest-pending-OTP escape hatch every 5s so the owner sees
  // Ramzi's code the moment he taps "Request OTP", regardless of whether
  // the email actually landed. Silences any 401/403 for non-owner users.
  const loadPendingOtp = async () => {
    try {
      const r = await api.get("/advisor-auth/latest-otp");
      setPendingOtp(r.data?.otp || null);
    } catch { /* not owner or expired */ }
  };
  useEffect(() => {
    load();
    if (isOwner) {
      loadPendingOtp();
      const iv = setInterval(loadPendingOtp, 5000);
      return () => clearInterval(iv);
    }
  }, [isOwner]);

  const copyOtp = () => {
    if (!pendingOtp?.code) return;
    try {
      navigator.clipboard.writeText(pendingOtp.code);
      toast.success("OTP copied. Share it with the advisor.");
    } catch { toast.error("Copy failed."); }
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-10">
        <p className="font-mono-brand text-propertic-gold mb-2">{t("admin.overview")}</p>
        <h1 className="font-editorial text-4xl text-propertic-navy">The Propertic Console</h1>
      </div>

      {isOwner && pendingOtp && (
        <div className="mb-8 rounded-2xl border border-propertic-gold/40 bg-propertic-sand/60 p-5 flex flex-wrap items-center gap-4" data-testid="dashboard-pending-otp-card">
          <ShieldCheck className="w-5 h-5 text-propertic-gold shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-mono-brand text-[10px] tracking-[0.14em] uppercase text-propertic-gold mb-0.5">Pending advisor OTP</p>
            <p className="text-sm text-propertic-navy">
              <span className="font-editorial text-lg">{pendingOtp.advisor_label}</span>
              <span className="text-propertic-muted"> · requested {new Date(pendingOtp.created_at).toLocaleTimeString()}</span>
            </p>
          </div>
          <div className="inline-flex items-center gap-2 bg-white border hairline rounded-xl px-4 h-11 font-editorial text-2xl tracking-[0.35em] tabular text-propertic-navy" data-testid="dashboard-pending-otp-code">{pendingOtp.code}</div>
          <button onClick={copyOtp} data-testid="dashboard-copy-otp-btn" className="h-11 px-4 rounded-full bg-propertic-navy text-propertic-paper text-[11px] font-mono-brand tracking-[0.14em] uppercase inline-flex items-center gap-2 hover:bg-propertic-navy-2">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={Users} label={t("admin.total_leads")} value={stats.total} testid="stat-total-leads" />
        <StatCard icon={TrendingUp} label="New this week" value={stats.by_status?.New || 0} testid="stat-new" />
        <StatCard icon={MessageCircle} label="WhatsApp clicks" value={traffic?.clicks_by_type?.whatsapp ?? "—"} testid="stat-whatsapp" />
        <StatCard icon={Phone} label="Call clicks" value={traffic?.clicks_by_type?.call ?? "—"} testid="stat-call" />
      </div>

      <div className="bg-white rounded-2xl border hairline p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-editorial text-2xl text-propertic-navy">{t("admin.pipeline")}</h2>
          <Link to="/admin/leads" className="font-mono-brand text-propertic-gold hover:text-propertic-gold-hover">View all →</Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {LEAD_STATUSES.map((s) => (
            <div key={s} className="border hairline rounded-lg p-3">
              <p className="font-mono-brand text-propertic-muted mb-1 truncate">{LEAD_STATUS_LABEL[lang][s]}</p>
              <p className="font-editorial text-2xl text-propertic-navy tabular">{stats.by_status?.[s] || 0}</p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <p className="font-mono-brand text-propertic-muted mb-3">Recent leads</p>
          <div className="divide-y hairline">
            {recent.length === 0 && <p className="text-propertic-muted text-sm">{t("admin.no_leads")}</p>}
            {recent.map((l) => (
              <Link
                key={l.id}
                to={`/admin/leads/${l.id}`}
                className="flex items-center justify-between py-3 hover:bg-propertic-cream/50 transition-colors -mx-2 px-2 rounded-lg"
                data-testid={`recent-lead-${l.id}`}
              >
                <div>
                  <p className="text-sm text-propertic-navy font-medium">{l.full_name}</p>
                  <p className="text-[11px] text-propertic-muted">{l.email} · {l.source}</p>
                </div>
                <span className="font-mono-brand text-propertic-gold border border-propertic-gold/30 bg-propertic-cream px-2 py-1 rounded-full">{LEAD_STATUS_LABEL[lang][l.status]}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {isOwner && <DLDRefreshPanel />}
      {isOwner && <RoniModelPanel />}
      {isOwner && <RoniHealthPanel />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border hairline p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="font-mono-brand text-propertic-muted">{label}</p>
        <Icon className="w-4 h-4 text-propertic-gold" />
      </div>
      <p className="font-editorial text-4xl mt-3 text-propertic-navy tabular">{value}</p>
    </div>
  );
}
